import { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, IconButton, Typography } from '@mui/material';

import MessagesWidgetComponent from '../components/MessagesWidgetComponent';

import {
  cancelNewChatCreation,
  checkDocumentExistingThread,
  checkDocumentNewThread,
  confirmSendingDocumentOnly,
  confirmSendingMultiRecipients,
  createThread,
  dropMessages,
  getChatById,
  getChatInfoById,
  getNumberOfRecipients,
  promptForReply,
  putNewMessage,
  setDocumentAsAttachment,
  setNewMessageText,
} from '../actions';

import { getLinkToProperty, getLinkToUser } from '../utils';

import * as commonSelectors from '../../Shared/selectors';

import StyledLink from '../../StyledLink';

const MessagesWidgetContainer = ({
  currentChatId,
  getThreadsList,
  getChatInfoById,
  chats,
  newChatData,
  buildings,
  createThread,
  putNewMessage,
  checkDocumentNewThread,
  checkDocumentExistingThread,
  cancelNewChatCreation,
  getChatById,
  history,
  baseUrl,
  getNumberOfRecipients,
  promptForReply,
  attachmentDocumentId,
  attachmentDocumentName,
  newMessageText,
  setDocumentAsAttachment,
  setNewMessageText,
  confirmSendingDocumentOnly,
  confirmSendingMultiRecipients,
  dropMessages,
  selectValue,
  setSelectValue,
  setSearch,
  isMobileScreen,
}) => {
  const [isInputDisabled, setIsInputDisabled] = useState(true);
  const [titleWithUrl, setTitleWithUrl] = useState(null);
  const [subtitleWithUrl, setSubtitleWithUrl] = useState(null);
  const [widgetHeader, setWidgetHeader] = useState({});
  const [recipientsCount, setRecipientsCount] = useState(null);
  const [isAttachIconShown, setIsAttachIconShown] = useState(false);
  const [attachModalType, setAttachModalType] = useState(null);
  const [isClosed, setIsClosed] = useState(false);

  const clearInputValue = () => {
    const input = document.getElementById('message_input');
    if (input?.value) {
      input.value = '';
    }
  };

  const turnInputOn = () => setIsInputDisabled(false);

  const turnInputOff = () => setIsInputDisabled(true);

  const renderChatById = (id) => {
    if (!id) return;

    cancelNewChatCreation();
    dropMessages();
    clearInputValue();
    turnInputOn();

    const chatMessages = getChatById(id);
    const chatInfo = getChatInfoById(id);
    Promise.all([chatInfo, chatMessages]).then((values) => {
      const chatInfoValues = get(values[0], 'data.results');
      // eslint-disable-next-line no-use-before-define
      getTitleAndSubtitle(id, chatInfoValues, false);
    });
  };

  const clearCurrentChat = () => {
    setTitleWithUrl(null);
    setSubtitleWithUrl(null);
    setWidgetHeader({});
    turnInputOff();
    clearInputValue();
  };

  const onRefresh = (updateChats) => {
    const building =
      typeof selectValue?.value === 'number' ? selectValue?.value : null;
    updateChats && getThreadsList({ building }, true);
    dropMessages();
    clearInputValue();
    if (currentChatId) {
      renderChatById(currentChatId);
    } else {
      clearCurrentChat();
    }
  };

  const getWidgetUrls = (recipient, scope, currentChatData, title) => {
    let titleUrlValue;
    let subtitleUrlValue;
    if (scope === 'Personal' && recipient) {
      titleUrlValue = getLinkToUser(recipient);
      subtitleUrlValue = getLinkToProperty(recipient, buildings);
    } else if (currentChatData && title) {
      titleUrlValue = (
        <StyledLink to={`/building/${currentChatData.building}`}>
          {title}
        </StyledLink>
      );
      subtitleUrlValue = null;
    }
    setTitleWithUrl(titleUrlValue);
    setSubtitleWithUrl(subtitleUrlValue);
  };

  const getTitleAndSubtitle = (
    id,
    chatsData = [],
    isNewChatCreation = true,
  ) => {
    let title;
    let subtitle;
    let recipient;

    const allChats = chatsData || (chats && chats.length ? chats : []);
    const currentChatData = allChats.filter((chat) => chat.id === id)[0];

    let recipientsCount = currentChatData && currentChatData.recipientsCount;
    if (!isEmpty(newChatData) && newChatData.isReply && isNewChatCreation) {
      title = (
        <Box display="flex" width="100%" gap="2rem" alignItems="center">
          <IconButton
            onClick={() => onRefresh()}
            edge="end"
            aria-label="Back"
            size="large"
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography
            align="center"
            variant="h5"
            color="primary"
            width="100%"
            noWrap
          >
            {`Reply to ${
              newChatData.messageTo && newChatData.messageTo.join(', ')
            }`}
          </Typography>
        </Box>
      );
      subtitle = getLinkToProperty(newChatData.person, buildings);
      turnInputOn();
    } else if (!isEmpty(newChatData) && isNewChatCreation) {
      recipientsCount = get(newChatData, 'numberOfRecipients', '...');
      title = (
        <Box width="100%">
          <Typography
            align="center"
            variant="h5"
            color="primary"
          >{`New message to ${
            newChatData.messageTo && newChatData.messageTo.join(', ')
          }`}</Typography>
        </Box>
      );
      subtitle = `${recipientsCount} recipients`;
      turnInputOn();
    } else if (!isEmpty(currentChatData)) {
      recipient = get(currentChatData, 'recipient');
      if (recipient) {
        recipient.propertyName = get(currentChatData, 'propertyName');
      }

      switch (currentChatData?.messageScope?.name) {
        case 'Personal':
          title = `${currentChatData.recipient.firstName} ${currentChatData.recipient.lastName}`;
          subtitle = currentChatData.propertyName;
          break;
        default:
          title = `${currentChatData?.messageScope?.name} @ ${currentChatData?.propertyName}`;
          subtitle = `${currentChatData?.recipientsCount} recipients`;
      }
    }
    const isClosed = get(currentChatData, 'closed', false);
    setIsClosed(isClosed);

    const widgetHeader = {
      title,
      subtitle: isClosed ? 'Moved Out' : subtitle,
      placeholder: isClosed ? 'This thread is closed' : null,
      isClosed,
    };

    const showAttachIcon =
      currentChatData?.building ||
      newChatData?.recipients ||
      newChatData?.groups;

    setWidgetHeader(widgetHeader);
    setRecipientsCount(recipientsCount);
    setIsAttachIconShown(!!showAttachIcon);

    isClosed && turnInputOff();

    getWidgetUrls(
      recipient,
      currentChatData?.messageScope?.name,
      currentChatData,
      title,
    );
  };

  // review if this function can be replaced
  const getNewChatRequestData = (values) => {
    const newChatRequestData = values || newChatData;
    const { recipients, groups, messageTo } = newChatRequestData;
    return { recipients, groups, messageTo };
  };

  const createNewThread = (data) => {
    setSearch(null); // set null to do not run onSearch
    clearCurrentChat();
    cancelNewChatCreation();

    createThread(data).then((threadIds) => {
      const [newChatId] = threadIds; // take the newest thread id
      newChatId && history.replace(`${baseUrl}/messages/${newChatId}`); // and open it
      setSelectValue({ value: 'all_properties', label: 'All properties' }); // fetches threads list after reset
    });
  };

  const handleNewUserMessage = (
    value = newMessageText,
    file = attachmentDocumentId,
    newReplyData,
    isDocumentOnly = false,
  ) => {
    const text = document.getElementById('message_input')?.value || value;
    let newChatValues;
    if (!isEmpty(newChatData)) {
      newChatValues = newChatData;
    }
    if (!isEmpty(newReplyData)) {
      newChatValues = newReplyData;
    }
    if (file && !text && !isDocumentOnly) {
      return confirmSendingDocumentOnly(
        () => handleNewUserMessage(undefined, undefined, undefined, true),
        attachmentDocumentName,
      );
    }
    if (!isEmpty(newChatValues)) {
      const data = getNewChatRequestData(newChatValues);
      if (text) {
        data.text = text;
      }
      if (file) {
        data.document = file;
      }

      createNewThread(data);
    } else {
      const data = {};
      if (!text && !file) return null;
      if (text) {
        data.text = text;
      }
      if (file) {
        data.document = file;
      }
      putNewMessage(currentChatId, data).then(() => onRefresh(true));
    }
    setDocumentAsAttachment(null, null);
    setNewMessageText(null);
    return null;
  };

  const checkNewAttachment = (documentId) => {
    const data = getNewChatRequestData();
    data.document = documentId;
    return currentChatId && isEmpty(newChatData)
      ? checkDocumentExistingThread(data, currentChatId)
      : checkDocumentNewThread(data);
  };

  const onReply = (message) => {
    promptForReply(message, handleNewUserMessage);
  };

  useEffect(() => {
    dropMessages();
  }, []);

  useEffect(() => {
    onRefresh();
  }, [currentChatId]);

  useEffect(() => {
    if (currentChatId && isInputDisabled) turnInputOn();
  });

  useEffect(() => {
    if (
      !isEmpty(newChatData) &&
      get(newChatData, 'numberOfRecipients') === undefined
    ) {
      dropMessages();
      const data = getNewChatRequestData();
      getNumberOfRecipients(data);
    }
    if (newChatData?.numberOfRecipients === 1 && newChatData?.isReply) {
      dropMessages();
    }
    getTitleAndSubtitle();
  }, [newChatData]);

  useEffect(
    () => () => {
      cancelNewChatCreation();
    },
    [],
  );

  return (
    <MessagesWidgetComponent
      currentChatId={currentChatId}
      handleNewUserMessage={handleNewUserMessage}
      widgetHeader={widgetHeader}
      isInputDisabled={isInputDisabled}
      recipientsCount={recipientsCount}
      isAttachIconShown={isAttachIconShown}
      attachModalType={attachModalType}
      setAttachModalType={setAttachModalType}
      titleWithUrl={titleWithUrl}
      subtitleWithUrl={subtitleWithUrl}
      checkNewAttachment={checkNewAttachment}
      chats={chats}
      attachmentDocumentId={attachmentDocumentId}
      attachmentDocumentName={attachmentDocumentName}
      setDocumentAsAttachment={setDocumentAsAttachment}
      confirmSendingMultiRecipients={confirmSendingMultiRecipients}
      newMessageText={newMessageText}
      onReply={onReply}
      isClosed={isClosed}
      newChatData={newChatData}
      isMobileScreen={isMobileScreen}
      cancelNewChatCreation={cancelNewChatCreation}
    />
  );
};

MessagesWidgetContainer.propTypes = {
  setSearch: PropTypes.func.isRequired,
  setSelectValue: PropTypes.func.isRequired,
  isMobileScreen: PropTypes.bool.isRequired,
  currentChatId: PropTypes.number,
  getThreadsList: PropTypes.func,
  getChatInfoById: PropTypes.func,
  chats: PropTypes.instanceOf(Array),
  newChatData: PropTypes.instanceOf(Object),
  buildings: PropTypes.instanceOf(Object),
  createThread: PropTypes.func,
  putNewMessage: PropTypes.func,
  checkDocumentNewThread: PropTypes.func,
  checkDocumentExistingThread: PropTypes.func,
  cancelNewChatCreation: PropTypes.func,
  getChatById: PropTypes.func,
  history: PropTypes.instanceOf(Object),
  baseUrl: PropTypes.string,
  getNumberOfRecipients: PropTypes.func,
  promptForReply: PropTypes.func,
  attachmentDocumentId: PropTypes.string,
  attachmentDocumentName: PropTypes.string,
  newMessageText: PropTypes.string,
  setDocumentAsAttachment: PropTypes.func,
  setNewMessageText: PropTypes.func,
  confirmSendingDocumentOnly: PropTypes.func,
  confirmSendingMultiRecipients: PropTypes.func,
  dropMessages: PropTypes.func,
  selectValue: PropTypes.instanceOf(Object),
};

const mapStateToProps = (state, ownProps) => {
  const {
    messages: {
      results: chats,
      newMessage: {
        documentId: attachmentDocumentId,
        documentName: attachmentDocumentName,
        text: newMessageText,
      },
    },
    buildings,
    newChatData,
  } = state;
  const currentChatId = Number(get(ownProps, 'match.params.id', null));
  const isMobileScreen = commonSelectors.getIsMobileScreen(state);
  return {
    currentChatId,
    chats,
    newChatData,
    buildings,
    attachmentDocumentId,
    attachmentDocumentName,
    newMessageText,
    isMobileScreen,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  createThread: (data, queryParams) =>
    createThread(dispatch, data, queryParams),
  putNewMessage: (chatId, data) => putNewMessage(dispatch, chatId, data),
  checkDocumentNewThread: (data) => checkDocumentNewThread(dispatch, data),
  checkDocumentExistingThread: (data, threadId) =>
    checkDocumentExistingThread(dispatch, data, threadId),
  cancelNewChatCreation: () => cancelNewChatCreation(dispatch),
  getChatInfoById: (chatId) => getChatInfoById(dispatch, chatId, ownProps),
  getChatById: (chatId) => getChatById(dispatch, chatId, { limit: 10 }),
  getNumberOfRecipients: (data) => getNumberOfRecipients(dispatch, data),
  promptForReply: (data, callback) => promptForReply(dispatch, data, callback),
  setDocumentAsAttachment: (documentId, documentName) =>
    setDocumentAsAttachment(dispatch, documentId, documentName),
  setNewMessageText: (text) => setNewMessageText(dispatch, text),
  confirmSendingDocumentOnly: (sendDocumentOnly, attachmentDocumentName) =>
    confirmSendingDocumentOnly(
      dispatch,
      sendDocumentOnly,
      attachmentDocumentName,
    ),
  confirmSendingMultiRecipients: (recipientsCount, onSendMessage) =>
    confirmSendingMultiRecipients(dispatch, recipientsCount, onSendMessage),
  dropMessages: () => dropMessages(dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MessagesWidgetContainer);
